import React from 'react';
import { Cookie, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import Layout from '../../layouts/Layout';

const CookiePolicy = () => {
    const lastUpdated = "7th July 2025";

    return (
        <Layout
            title="Cookie Policy"
            description="KGC Innovations Ltd Cookie Policy - How we use cookies and tracking technologies on our website. Google Analytics usage and user privacy information."
            keywords="cookie policy, KGC Innovations, cookies, Google Analytics, tracking, privacy, user preferences, web development UK"
            path="/cookie-policy"
        >
            <div className="min-h-screen bg-white">
                {/* Header */}
                <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-slate-50 to-slate-100">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center opacity-0 animate-[fadeInUp_0.8s_ease-out_0.2s_forwards]">
                            <div className="inline-flex items-center px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-8">
                                <Cookie className="w-4 h-4 mr-2" />
                                Cookie Information
                            </div>

                            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                                Cookie Policy
                            </h1>

                            <p className="text-xl text-slate-600 mb-4">
                                How we use cookies and similar technologies on our website
                            </p>

                            <div className="flex items-center justify-center text-slate-500">
                                <Calendar className="w-4 h-4 mr-2" />
                                Last updated: {lastUpdated}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content */}
                <section className="py-16 bg-white">
                    <div className="max-w-4xl mx-auto px-6">
                        <div className="prose prose-lg prose-slate max-w-none">

                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 mb-12">
                                <h2 className="text-2xl font-bold text-slate-900 mb-4">Introduction</h2>
                                <p className="text-slate-700 leading-relaxed">
                                    This Cookie Policy explains how KGC Innovations Ltd uses cookies and similar technologies on our website kgcinnovations.com. By using our website, you consent to the use of cookies as described in this policy.
                                </p>
                            </div>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">1. What Are Cookies?</h2>
                            <p className="mb-8">
                                Cookies are small text files that are stored on your device when you visit a website. They help websites function properly and provide information to website owners about how their site is being used.
                            </p>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">2. Types of Cookies We Use</h2>

                            <div className="space-y-8">
                                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Essential Cookies</h3>
                                    <p className="text-slate-700 mb-4">
                                        These cookies are necessary for the website to function properly and cannot be disabled.
                                    </p>
                                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                                        <p><strong>Purpose:</strong> User preferences and settings</p>
                                        <p><strong>Duration:</strong> Session and persistent</p>
                                        <p><strong>Third Party:</strong> No</p>
                                    </div>
                                </div>

                                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                    <h3 className="text-xl font-semibold text-slate-900 mb-4">Analytics Cookies</h3>
                                    <p className="text-slate-700 mb-4">
                                        These cookies help us understand how visitors interact with our website by collecting information anonymously.
                                    </p>
                                    <div className="bg-white rounded-lg p-4 border border-slate-200">
                                        <p><strong>Provider:</strong> Google Analytics</p>
                                        <p><strong>Purpose:</strong> Website usage analytics, performance monitoring</p>
                                        <p><strong>Duration:</strong> Up to 26 months</p>
                                        <p><strong>Third Party:</strong> Yes (Google)</p>
                                        <p><strong>Data Collected:</strong> Page views, session duration, bounce rate, device information</p>
                                    </div>
                                </div>
                            </div>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">3. Google Analytics</h2>
                            <p className="mb-6">
                                We use Google Analytics to analyze website traffic and improve our services. Google Analytics uses cookies to collect information about your use of our website, including:
                            </p>
                            <ul className="list-disc ml-6 mb-6">
                                <li>Pages you visit and time spent on each page</li>
                                <li>How you arrived at our website</li>
                                <li>Your approximate geographic location</li>
                                <li>Device and browser information</li>
                                <li>Interactions with our content</li>
                            </ul>
                            <p className="mb-8">
                                This information is processed by Google and may be transferred to Google servers outside the EU/UK. Google has committed to adequate data protection measures. You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" className="text-slate-900 underline hover:text-slate-700" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
                            </p>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">4. Managing Your Cookie Preferences</h2>

                            <h3 className="text-xl font-semibold text-slate-900 mb-4">Browser Settings</h3>
                            <p className="mb-6">
                                You can control and delete cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of our website.
                            </p>

                            <h3 className="text-xl font-semibold text-slate-900 mb-4">Browser-Specific Instructions</h3>
                            <ul className="list-disc ml-6 mb-8">
                                <li><strong>Chrome:</strong> Settings {'>'} Privacy and security {'>'} Cookies and other site data</li>
                                <li><strong>Firefox:</strong> Options {'>'} Privacy & Security {'>'} Cookies and Site Data</li>
                                <li><strong>Safari:</strong> Preferences {'>'} Privacy {'>'} Manage Website Data</li>
                                <li><strong>Edge:</strong> Settings {'>'} Cookies and site permissions</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">5. Third-Party Cookies</h2>
                            <p className="mb-6">
                                Our website may contain links to third-party websites or embed third-party content. These third parties may use their own cookies, which are governed by their respective privacy policies:
                            </p>
                            <ul className="list-disc ml-6 mb-8">
                                <li><strong>Google Analytics:</strong> <a href="https://policies.google.com/privacy" className="text-slate-900 underline hover:text-slate-700" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
                                <li><strong>Payment Processors:</strong> Stripe and GoCardless cookies for payment processing</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">6. Cookie Consent</h2>
                            <p className="mb-6">
                                By continuing to use our website, you consent to our use of cookies as described in this policy. You can withdraw your consent at any time by:
                            </p>
                            <ul className="list-disc ml-6 mb-8">
                                <li>Adjusting your browser cookie settings</li>
                                <li>Using browser opt-out tools</li>
                                <li>Contacting us at legal@kgcinnovations.com</li>
                            </ul>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">7. Updates to This Policy</h2>
                            <p className="mb-8">
                                We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website with a new "last updated" date.
                            </p>

                            <h2 className="text-2xl font-bold text-slate-900 mb-6">8. Contact Information</h2>
                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                                <p className="mb-4">If you have questions about our use of cookies, please contact us:</p>
                                <p>
                                    KGC Innovations Ltd<br />
                                    Company Registration: 16570829<br />
                                    Registered Office: Unit A, 82 James Carter Road, Mildenhall, IP28 7DE, England<br />
                                    Email: legal@kgcinnovations.com<br />
                                    Phone: +44 (0) 20 7046 0289
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Contact Section */}
                <section className="py-16 bg-slate-50">
                    <div className="max-w-4xl mx-auto px-6 text-center">
                        <h2 className="text-3xl font-bold text-slate-900 mb-6">Questions About Our Cookie Policy?</h2>
                        <p className="text-lg text-slate-600 mb-8">
                            Need help managing your cookie preferences or have privacy concerns? Contact us.
                        </p>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-xl p-6 border border-slate-200">
                                <Mail className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                                <div className="font-semibold text-slate-900 mb-1">Compliance</div>
                                <div className="text-slate-600">legal@kgcinnovations.com</div>
                            </div>
                            <div className="bg-white rounded-xl p-6 border border-slate-200">
                                <Phone className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                                <div className="font-semibold text-slate-900 mb-1">Phone</div>
                                <div className="text-slate-600">+44 (0) 20 7046 0289</div>
                            </div>
                            <div className="bg-white rounded-xl p-6 border border-slate-200">
                                <MapPin className="w-8 h-8 text-slate-900 mx-auto mb-3" />
                                <div className="font-semibold text-slate-900 mb-1">Registered Office</div>
                                <div className="text-slate-600 text-sm">England & Wales</div>
                            </div>
                        </div>
                    </div>
                </section>

                <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-[fadeInUp_0.8s_ease-out_0.2s_forwards] {
                    animation: fadeInUp 0.8s ease-out 0.2s forwards;
                }
            `}</style>
            </div>
        </Layout>
    );
};

export default CookiePolicy;